import dispatchCustomEvent from '../../events/dispatchCustomEvent';

/*
 * EVENT HANDLING
 *
 * Instead of functions calling other functions, and adding in many `if` statements to handle our
 * various conditions and logic for new forms, the form is instead very event-driven, and events
 * can be intercepted as they bubble up from their triggers.
 *
 * So we don't tell the wizard to navigate, etc. We raise an event like `formWizard.navigate` on
 * the current element which bubbles up to the form. It then decides how it wants to respond to
 * a request to navigate. Because of the bubbling nature of events, panels can intercept them
 * and perform validation checks, cancelling the event if need be.
 */

export const events = {
  completeStep: 'formWizard.completeStep',
  customError: 'formWizard.customError',
  hideStep: 'formWizard.hideStep',
  navigate: 'formWizard.navigate',
  retrieveFieldValue: 'formWizard.retrieveFieldValue',
  showStep: 'formWizard.showStep'
};

/**
 * Dispatches an event to tell the wizard to navigate to another panel or action.
 *
 * @param {Element} target - The button or panel that triggers navigation
 * @param {Object} options
 * @param {string} options.direction - The direction to navigate ('forwards' or 'backwards')
 * @param {Element} options.toStep - Optionally, the specific panel or action to navigate to
 * @returns {Event}
 */
export function dispatchNavigateEvent(target, { direction, toStep } = {}) {
  return dispatchCustomEvent(target, events.navigate, { direction, toStep });
}

/**
 * Dispatches an event to tell any listeners that a panel has been completed,
 * typically for tracking purposes.
 *
 * @param {Element} target - The panel that has been completed
 * @returns {Event}
 */
export function dispatchCompleteStepEvent(target) {
  return dispatchCustomEvent(target, events.completeStep);
}

/**
 * Dispatches an event to tell a panel to come into view.
 *
 * @param {Element} target - The panel that must be shown.
 * @param {Object} options
 * @param {string} options.direction - The direction of navigation ('forwards' or 'backwards')
 * @param {string} options.fromPanel - The panel that we are navigating from
 * @returns {Event}
 */
export function dispatchShowStepEvent(target, { direction, fromPanel } = {}) {
  return dispatchCustomEvent(target, events.showStep, { direction, fromPanel });
}

/**
 * Dispatches an event to tell a panel to move out of view.
 *
 * @param {Element} target - The panel that must be hidden.
 * @param {Object} options
 * @param {string} options.direction - The direction of navigation ('forwards' or 'backwards')
 * @returns {Event}
 */
export function dispatchHideStepEvent(target, { direction } = {}) {
  return dispatchCustomEvent(target, events.hideStep, { direction });
}

/**
 * Dispatches an event to tell a field that we are retrieving its value,
 * which allows the field to optionally modify the value.
 *
 * @param {Element} target - The input field we are querying.
 * @param {Object} options
 * @param {*} options.value - The value we pulled from the input.
 * @returns {Event}
 */
export function dispatchRetrieveFieldValueEvent(target, { value } = {}) {
  return dispatchCustomEvent(target, events.retrieveFieldValue, { value });
}

/**
 * Dispatches an event to tell a panel to render a custom error (typically submission failure).
 *
 * @param {Element} target - The panel to show the error on
 * @param {Error} error - The error object to dispatch as the event detail
 * @returns {Event}
 */
export function dispatchCustomErrorEvent(target, error) {
  return dispatchCustomEvent(target, events.customError, error);
}
